import {
  LogLevel,
  PublicClientApplication,
  type Configuration,
  type PopupRequest,
  type RedirectRequest,
  type SilentRequest,
} from "@azure/msal-browser";

import { getRuntimeConfig } from "./runtimeConfig";

/**
 * Build an MSAL client using the runtime configuration. Must be called
 * AFTER `loadRuntimeConfig()` has resolved.
 */
export function createMsalClient(): PublicClientApplication {
  const cfg = getRuntimeConfig();

  const configuration: Configuration = {
    auth: {
      clientId: cfg.clientId,
      authority: cfg.authority,
      knownAuthorities: cfg.knownAuthority ? [cfg.knownAuthority] : [],
      redirectUri: cfg.redirectUri,
      postLogoutRedirectUri: cfg.postLogoutRedirectUri,
      // We handle the post-login navigation in AuthCallbackPage.
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: "sessionStorage",
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if (containsPii) return;
          if (level === LogLevel.Error) console.error("[msal]", message);
        },
        logLevel: LogLevel.Error,
      },
    },
  };

  return new PublicClientApplication(configuration);
}

export function getLoginRequest(): RedirectRequest {
  const cfg = getRuntimeConfig();
  return {
    scopes: cfg.scopes,
    prompt: "select_account",
  };
}

export function getGraphTokenRequest(account?: unknown): SilentRequest {
  const cfg = getRuntimeConfig();
  const scopes = cfg.graphScopes.length
    ? cfg.graphScopes
    : ["User.Read", "User.ReadWrite"];
  return {
    scopes,
    account: (account ?? undefined) as SilentRequest["account"],
  };
}

export function getGraphPopupRequest(): PopupRequest {
  const cfg = getRuntimeConfig();
  const scopes = cfg.graphScopes.length
    ? cfg.graphScopes
    : ["User.Read", "User.ReadWrite"];
  return { scopes, prompt: "consent" };
}

/**
 * Build a silent token request for an arbitrary scope set. Used by
 * flows that need a different permission than the default graph
 * scopes (e.g. Directory.AccessAsUser.All for /me/changePassword).
 */
export function getTokenRequestForScopes(
  scopes: string[],
  account?: unknown,
): SilentRequest {
  return {
    scopes,
    account: (account ?? undefined) as SilentRequest["account"],
  };
}

export function getPopupRequestForScopes(scopes: string[]): PopupRequest {
  return { scopes, prompt: "consent" };
}

/**
 * Scope required by the Microsoft Graph `POST /me/changePassword`
 * action for a user to change their own password.
 *
 *   https://learn.microsoft.com/graph/api/user-changepassword
 *
 * Admin consent is required for this permission on the SPA app
 * registration. Works for Workforce Entra AND External ID (CIAM) when
 * the tenant admin has consented.
 */
export const CHANGE_PASSWORD_SCOPES = ["Directory.AccessAsUser.All"];
